import { useState } from 'react';
import { KM_PER_MILE, mileageMismatch, formatMileage } from '../../utils/dateUtils';

/**
 * Mileage entry with a km/mi toggle.
 *
 * The value passed in and out is ALWAYS kilometres — km is canonical for owned
 * vehicles, so switching the toggle only changes what you type and see, never
 * what gets stored.
 *
 * Typed text is held locally so converting on every keystroke can't fight the
 * user (round-tripping through km and back would nudge digits as they type).
 */
export const MileageInput = ({ value, onChange, label = 'Mileage', placeholder, required = false, defaultUnit = 'km' }) => {
  const [unit, setUnit] = useState(defaultUnit === 'mi' ? 'mi' : 'km');
  const [text, setText] = useState(() => (value == null || value === '' ? '' : String(value)));

  const switchUnit = (next) => {
    if (next === unit) return;
    if (text !== '' && isFinite(Number(text))) {
      const n = Number(text);
      const km = unit === 'km' ? n : n * KM_PER_MILE;
      setText(String(Math.round(next === 'km' ? km : km / KM_PER_MILE)));
    }
    setUnit(next);
  };

  const handle = (raw) => {
    setText(raw);
    if (raw === '') { onChange(''); return; }
    const n = Number(raw);
    if (!isFinite(n)) return;
    onChange(String(Math.round(unit === 'km' ? n : n * KM_PER_MILE)));
  };

  // The equivalent in the other unit, shown underneath as a live hint.
  const n = Number(text);
  const hint = text !== '' && isFinite(n) && n > 0
    ? (unit === 'km'
        ? `≈ ${Math.round(n / KM_PER_MILE).toLocaleString()} miles`
        : `≈ ${Math.round(n * KM_PER_MILE).toLocaleString()} km  ·  stored in km`)
    : null;

  const btn = (u, txt) => (
    <button type="button" onClick={() => switchUnit(u)}
      style={{
        padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
        border: 'none', borderRadius: 6,
        background: unit === u ? 'var(--accent)' : 'transparent',
        color: unit === u ? '#fff' : 'var(--text3)',
        transition: 'var(--transition)',
      }}>{txt}</button>
  );

  return (
    <div className="form-group">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <label className="form-label" style={{ margin: 0 }}>{label}</label>
        <div style={{ display: 'flex', gap: 2, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: 2 }}>
          {btn('km', 'km')}
          {btn('mi', 'mi')}
        </div>
      </div>
      <input
        className="form-input"
        type="number"
        step="any"
        required={required}
        value={text}
        onChange={e => handle(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
};

/**
 * Shown when the vehicle's own mileage and the Collector Score "Current Mileage" disagree.
 * `thisLabel` / `otherLabel` name the two places so the owner knows where to look.
 */
export const MileageMismatchNotice = ({ thisKm, otherKm, thisLabel, otherLabel, style }) => {
  const m = mileageMismatch(thisKm, otherKm);
  if (!m) return null;
  return (
    <div role="alert" style={{
      fontSize: 12, lineHeight: 1.5, color: '#f97316', marginTop: 8, padding: '8px 10px',
      border: '1px solid rgba(249,115,22,0.35)', background: 'rgba(249,115,22,0.08)', borderRadius: 8, ...style,
    }}>
      ⚠ <strong>Mileage doesn’t match.</strong> {thisLabel} shows {formatMileage(thisKm)}, but {otherLabel} shows {formatMileage(otherKm)}.
      {m.unitMixup && ' The gap looks like a km / miles mix-up.'}
      {' '}Please check which one is correct and fix the other.
    </div>
  );
};
